// src/components/ProductReviews.tsx
//
// Published customer reviews for one product. Renders the average-star summary
// under the product title and, when there are reviews, an expandable list.
// Reviews are written from the account order page (verified buyers only) and
// only appear here after admin approval — see docs/product-reviews.md.

import React, { useEffect, useState } from "react";
import { getProductReviews, ProductReviewsSummary } from "../api";

const Stars: React.FC<{ value: number }> = ({ value }) => (
  <span aria-label={`${value} out of 5 stars`} className="whitespace-nowrap">
    {[1, 2, 3, 4, 5].map((star) => (
      <span
        key={star}
        className={`text-lg ${
          star <= Math.round(value) ? "text-yellow-500" : "text-gray-300"
        }`}
      >
        ★
      </span>
    ))}
  </span>
);

type ProductReviewsProps = {
  /** Square catalog ITEM id. */
  itemId?: string;
};

const ProductReviews: React.FC<ProductReviewsProps> = ({ itemId }) => {
  const [summary, setSummary] = useState<ProductReviewsSummary | null>(null);
  const [isListOpen, setIsListOpen] = useState(false);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    getProductReviews(itemId).then((res) => {
      if (!cancelled && res?.data && !res.error) {
        setSummary(res.data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  // No reviews yet (or still loading): render nothing rather than "0 reviews" —
  // an empty rating row reads as a bad rating.
  if (!summary || summary.count === 0 || summary.averageRating === null) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        className="flex items-center gap-2 self-start"
        onClick={() => setIsListOpen((open) => !open)}
        aria-expanded={isListOpen}
      >
        <Stars value={summary.averageRating} />
        <span className="text-sm text-gray-600 underline">
          {summary.averageRating.toFixed(1)} ({summary.count} review
          {summary.count > 1 ? "s" : ""})
        </span>
      </button>

      {isListOpen && (
        <div className="flex flex-col gap-3 border-t border-gray-200 pt-3">
          {summary.reviews.map((review, i) => (
            <div key={i} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Stars value={review.rating} />
                <span className="text-sm font-semibold">{review.author}</span>
                <span className="text-xs text-gray-400">
                  {new Date(review.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
              {review.text && (
                <p className="text-sm text-gray-700">{review.text}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
